import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { fetchNegativeLead, updateNegativeLead, deleteNegativeLead, transferNegativeToPipeline } from '@/lib/api';
import { formatINR, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Trash2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

const REASONS = ["No Budget", "No Timing", "Wrong Fit", "No Response", "Competitor Won", "Duplicate", "Other"];

export default function NegativeLeadDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [lead, setLead] = useState(null);

    useEffect(() => { fetchNegativeLead(id).then(setLead); }, [id]);

    const save = async () => {
        try {
            const updated = await updateNegativeLead(id, {
                customer_name: lead.customer_name, contact_person: lead.contact_person, email: lead.email, mobile: lead.mobile,
                service_line: lead.service_line, service_offered: lead.service_offered, proposal_value: Number(lead.proposal_value),
                sales_owner: lead.sales_owner, area: lead.area, location: lead.location, source: lead.source,
                reason: lead.reason, notes: lead.notes,
            });
            setLead(updated);
            toast.success('Updated');
        } catch { toast.error('Update failed'); }
    };

    const transfer = async () => {
        if (!window.confirm(`Transfer "${lead.customer_name}" back to the active pipeline?`)) return;
        try {
            const r = await transferNegativeToPipeline(id);
            toast.success(`Transferred as ${r.new_lead_id}`);
            nav('/negative-leads');
        } catch { toast.error('Transfer failed'); }
    };

    const remove = async () => {
        if (!window.confirm('Permanently delete this negative lead?')) return;
        await deleteNegativeLead(id);
        toast.success('Deleted');
        nav('/negative-leads');
    };

    if (!lead) return <div className="p-8 text-slate-500">Loading…</div>;

    return (
        <div>
            <PageHeader
                title={lead.customer_name}
                subtitle={`${lead.lead_id} · ${lead.reason}`}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" className="border-[#E2E8F0] bg-transparent text-slate-900 hover:bg-[#FFFFFF]" onClick={() => nav('/negative-leads')} data-testid="btn-back">
                            <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <Button onClick={transfer} data-testid="btn-transfer" className="bg-[#016a6d] hover:bg-[#014e50] gap-2">
                            <ArrowRightLeft className="w-4 h-4" /> To Pipeline
                        </Button>
                        <Button onClick={save} data-testid="btn-save" className="bg-[#2d6b3d] hover:bg-[#255c33] gap-2">
                            <Save className="w-4 h-4" /> Save
                        </Button>
                        <Button variant="outline" className="border-[#8a2d2d] bg-transparent text-[#FF453A]" onClick={remove} data-testid="btn-delete">
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                }
            />
            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Section title="Deal">
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Customer" value={lead.customer_name} onChange={v => setLead({ ...lead, customer_name: v })} testId="ndetail-customer" />
                            <F label="Service Line" value={lead.service_line} onChange={v => setLead({ ...lead, service_line: v })} testId="ndetail-service" />
                            <F label="Service Offered" value={lead.service_offered} onChange={v => setLead({ ...lead, service_offered: v })} testId="ndetail-service-offered" />
                            <F label="Proposal Value (₹)" type="number" value={lead.proposal_value} onChange={v => setLead({ ...lead, proposal_value: v })} testId="ndetail-value" />
                            <F label="Sales Owner" value={lead.sales_owner} onChange={v => setLead({ ...lead, sales_owner: v })} testId="ndetail-owner" />
                            <S label="Reason" value={lead.reason} options={REASONS} onChange={v => setLead({ ...lead, reason: v })} testId="ndetail-reason" />
                            <F label="Source" value={lead.source} onChange={v => setLead({ ...lead, source: v })} testId="ndetail-source" />
                            <F label="Area" value={lead.area} onChange={v => setLead({ ...lead, area: v })} testId="ndetail-area" />
                        </div>
                    </Section>
                    <Section title="Contact">
                        <div className="grid grid-cols-2 gap-4">
                            <F label="Contact Person" value={lead.contact_person} onChange={v => setLead({ ...lead, contact_person: v })} testId="ndetail-contact" />
                            <F label="Email" value={lead.email} onChange={v => setLead({ ...lead, email: v })} testId="ndetail-email" />
                            <F label="Mobile" value={lead.mobile} onChange={v => setLead({ ...lead, mobile: v })} testId="ndetail-mobile" />
                            <F label="Location" value={lead.location} onChange={v => setLead({ ...lead, location: v })} testId="ndetail-location" />
                        </div>
                    </Section>
                    <Section title="Notes">
                        <Textarea rows={5} value={lead.notes || ''} onChange={e => setLead({ ...lead, notes: e.target.value })} data-testid="ndetail-notes" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900" />
                    </Section>
                </div>
                <div className="space-y-6">
                    <div className="surface-1 rounded-md p-6">
                        <div className="overline">Summary</div>
                        <div className="mt-4 space-y-3">
                            <Stat label="Proposal Value" value={formatINR(lead.proposal_value)} />
                            <Stat label="Reason" value={lead.reason} />
                            <Stat label="Owner" value={lead.sales_owner || '—'} />
                            <Stat label="Added" value={formatDate(lead.date_added)} />
                            <Stat label="Updated" value={formatDate(lead.updated_at)} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="surface-1 rounded-md p-6">
            <div className="overline mb-4">{title}</div>
            {children}
        </div>
    );
}
function F({ label, value, onChange, type = 'text', testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <Input value={value || ''} type={type} onChange={e => onChange(e.target.value)} data-testid={testId} className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
        </div>
    );
}
function S({ label, value, onChange, options, testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
function Stat({ label, value }) {
    return (
        <div className="flex items-center justify-between text-sm border-b border-[#E2E8F0] pb-3 last:border-0 last:pb-0">
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-900 text-right">{value}</span>
        </div>
    );
}
